import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaTwitter, FaInstagram, FaLinkedinIn, FaChevronRight } from 'react-icons/fa';
import logo from '../../assets/images/logo.svg';

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-dark-medium text-light relative overflow-hidden">
      {/* Footer top wave */}
      <div className="w-full bg-gradient-to-r from-dark via-dark-light to-dark h-12"></div>
      
      {/* Metal shine accent line */}
      <div className="h-1 w-full bg-gradient-to-r from-dark-accent via-accent to-dark-accent metal-shine"></div>
      
      {/* Main content */}
      <div className="container mx-auto px-4 py-16">
        {/* Top section with logo and social */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
          <div className="flex items-center mb-8 md:mb-0">
            <div className="h-16 w-16 bg-dark-accent/10 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-accent/20 mr-4">
              <img src={logo} alt="ÇelikSac Logo" className="h-10 w-auto" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold bg-gradient-to-r from-light to-secondary bg-clip-text text-transparent">ÇelikSac</h2>
              <p className="text-sm text-light/60">{t('footer.tagline', {year: new Date().getFullYear() - 1980})} {/* Years of experience */}</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-dark-accent/20 rounded-full text-light/70 hover:text-accent hover:bg-dark-accent/30 transition-all">
              <FaFacebook size={18} />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-dark-accent/20 rounded-full text-light/70 hover:text-accent hover:bg-dark-accent/30 transition-all">
              <FaTwitter size={18} />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-dark-accent/20 rounded-full text-light/70 hover:text-accent hover:bg-dark-accent/30 transition-all">
              <FaInstagram size={18} />
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-dark-accent/20 rounded-full text-light/70 hover:text-accent hover:bg-dark-accent/30 transition-all">
              <FaLinkedinIn size={18} />
            </a>
          </div>
        </div>
        
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          {/* Company Info */}
          <div>
            <h3 className="text-lg font-medium mb-6 text-accent border-b border-dark-accent/30 pb-2">{t('footer.companyInfo.title')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start group">
                <div className="p-2 mt-1 bg-dark-accent/20 rounded-full text-xs text-accent mr-3 group-hover:bg-accent/20 transition-all">
                  <FaMapMarkerAlt />
                </div>
                <span className="text-light/70 group-hover:text-light transition-colors">{t('footer.companyInfo.address')}</span>
              </li>
              <li className="flex items-center group">
                <div className="p-2 bg-dark-accent/20 rounded-full text-xs text-accent mr-3 group-hover:bg-accent/20 transition-all">
                  <FaPhone />
                </div>
                <span className="text-light/70 group-hover:text-light transition-colors">{t('footer.companyInfo.phone')}</span>
              </li>
              <li className="flex items-center group">
                <div className="p-2 bg-dark-accent/20 rounded-full text-xs text-accent mr-3 group-hover:bg-accent/20 transition-all">
                  <FaEnvelope />
                </div>
                <span className="text-light/70 group-hover:text-light transition-colors">{t('footer.companyInfo.email')}</span>
              </li>
            </ul>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-medium mb-6 text-accent border-b border-dark-accent/30 pb-2">{t('footer.quickLinks.title')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.quickLinks.home')}</span>
                </Link>
              </li>
              <li>
                <Link to="/about" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.quickLinks.about')}</span>
                </Link>
              </li>
              <li>
                <Link to="/products" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.quickLinks.products')}</span>
                </Link>
              </li>
              <li>
                <Link to="/certificates" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.quickLinks.certificates')}</span>
                </Link>
              </li>
              <li>
                <Link to="/contact" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.quickLinks.contact')}</span>
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Products */}
          <div>
            <h3 className="text-lg font-medium mb-6 text-accent border-b border-dark-accent/30 pb-2">{t('footer.products.title')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/products#harrow-discs" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.products.category1')}</span>
                </Link>
              </li>
              <li>
                <Link to="/products#spare-parts" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.products.category2')}</span>
                </Link>
              </li>
              <li>
                <Link to="/products#custom-metal" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.products.category3')}</span>
                </Link>
              </li>
              <li>
                <Link to="/products#custom-orders" className="flex items-center text-light/70 hover:text-light transition-colors group">
                  <FaChevronRight className="mr-2 text-xs opacity-50 group-hover:text-accent" />
                  <span>{t('footer.products.category4')}</span>
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Contact info */}
          <div>
            <h3 className="text-lg font-medium mb-6 text-accent border-b border-dark-accent/30 pb-2">{t('footer.contactUs.title')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="p-2 mt-1 bg-dark-accent/20 rounded-full text-xs text-accent mr-3">
                  <FaMapMarkerAlt />
                </div>
                <span className="text-light/70">{t('footer.contactUs.address')}</span>
              </li>
              <li className="flex items-center">
                <div className="p-2 bg-dark-accent/20 rounded-full text-xs text-accent mr-3">
                  <FaPhone />
                </div>
                <a href="tel:+903322390409" className="text-light/70 hover:text-light transition-colors">{t('footer.contactUs.phone1')}</a>
              </li>
              <li className="flex items-center">
                <div className="p-2 bg-dark-accent/20 rounded-full text-xs text-accent mr-3">
                  <FaPhone />
                </div>
                <a href="tel:+905544946406" className="text-light/70 hover:text-light transition-colors">{t('footer.contactUs.phone2')}</a>
              </li>
              <li className="flex items-center">
                <div className="p-2 bg-dark-accent/20 rounded-full text-xs text-accent mr-3">
                  <FaEnvelope />
                </div>
                <a href="mailto:info@celiksac.com.tr" className="text-light/70 hover:text-light transition-colors">{t('footer.contactUs.email')}</a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="border-t border-dark-accent/30 mt-12 pt-8 text-center">
          <p className="text-light/50 text-sm">{t('footer.copyright', {year: new Date().getFullYear()})}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
